/**
 * @fileoverview Seal credential values to the off-host operator using browser Web Crypto. Only the encrypted envelope goes to the app; card identity, type, name and revision are authenticated with the payload. `Credentials.js` owns transient form state; the operator tool verifies this context before decrypting an export. No secret is persisted in browser storage.
 */

export const MAX_CREDENTIAL_BYTES = 65536

function base64(bytes) {
    let binary = ''
    for (const byte of new Uint8Array(bytes)) binary += String.fromCharCode(byte)
    return btoa(binary)
}

export function validateCredential(value, provider) {
    if (!value.trim()) throw new Error('Paste a credential value before saving.')
    if (new TextEncoder().encode(value).length > MAX_CREDENTIAL_BYTES) throw new Error('Credentials must be smaller than 64 KB.')
    if (provider === 'google-service-account') {
        let parsed
        try { parsed = JSON.parse(value) } catch (_) { throw new Error('Paste the complete Google service account JSON file.') }
        if (parsed?.type !== 'service_account' || typeof parsed.client_email !== 'string' || !parsed.client_email.includes('@') || typeof parsed.private_key_id !== 'string' || !parsed.private_key_id || typeof parsed.private_key !== 'string' || !parsed.private_key.includes('-----BEGIN PRIVATE KEY-----')) {
            throw new Error('The JSON must contain a Google service account email, key ID and private key.')
        }
    }
}

export async function encryptCredential(value, card, receiver) {
    if (!window.crypto?.subtle) throw new Error('Open the app over HTTPS to save credentials.')
    const bytes = new TextEncoder().encode(value)
    const context = new TextEncoder().encode(JSON.stringify([card.id, card.provider, card.name, receiver.key_id, card.revision + 1]))
    const publicKey = Uint8Array.from(atob(receiver.public_key), (char) => char.charCodeAt(0))
    const recipient = await crypto.subtle.importKey('spki', publicKey, { name: 'RSA-OAEP', hash: 'SHA-256' }, false, ['wrapKey'])
    const aes = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt'])
    const iv = crypto.getRandomValues(new Uint8Array(12))
    try {
        const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: context, tagLength: 128 }, aes, bytes)
        const wrapped = await crypto.subtle.wrapKey('raw', aes, recipient, { name: 'RSA-OAEP' })
        return { algorithm: 'RSA-OAEP-256+A256GCM', key_id: receiver.key_id, wrapped_key: base64(wrapped), iv: base64(iv), ciphertext: base64(ciphertext) }
    } finally {
        bytes.fill(0)
    }
}
